<template>
  <div class="weather-page">
    <div class="weather-layout">
      <div class="weather-sidebar">
        <weather-table
          ref="weatherTable"
          :selected-index="selectedIndex"
          :weather-data="weatherData"
          :loading-map="loadingState"
          :has-route="hasRoute"
          @row-click="handleTableRowClick"
        />
      </div>
      <div class="weather-map">
        <div class="weather-map__header">
          <div class="ship-badge" :class="{ 'ship-badge--clickable': hasOwnShipPosition }" @click="centerMapOnShip">
            <span>{{ shipNameDisplay }}</span>
          </div>
        </div>
  <div ref="mapContainer" id="meteoblue-container" class="meteoblue-container" />
        <div v-if="!hasRoute" class="weather-map__placeholder">
          <i class="bi bi-info-circle mr-2" />
          {{ $t('routes.weatherNotAvailable') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { mapStores } from 'pinia';
import { useRoutesStore } from '@/stores/routes';
import { useDeviceStore } from '@/stores/device';
import { useFleetStore } from '@/stores/fleet';
import WeatherTable from './WeatherTable.vue';
import meteoblueLoader from '@/services/meteoblueLoader';
import { getWindyWeatherReal } from '@/my-leaf/routes/components/route-utils';
import { drawShipIcon } from '@/utils/shipIcon';

const METEOBLUE_API_KEY = '6MonjPXwB4DKEhHa';
const METEOBLUE_INVENTORY_URL = 'https://maps-api.meteoblue.com/v1/map/inventory/filter?lang=en&apikey=                                                                                                                                                                                                                                                                                                                                                                                                                                                                            &maps=windAnimation,radar,wind,warningsAndRisk,waveHeight&temperatureUnit=CELSIUS&velocityUnit=METER_PER_SECOND&lengthUnit=metric&overlays=pressure2mOverlay,graticuleOverlay';
const LOG_PREFIX = '[meteoblue-route]';

export default {
  name: 'MeteoblueWeatherMap',
  components: {
    WeatherTable
  },
  data() {
    return {
      mapboxMap: null,
      mapboxgl: null,
      meteobluePlugin: null,
      meteobluePluginLoadHandler: null,
      meteobluePluginMenuHandler: null,
      mapStyleLoadHandler: null,
      mapReady: false,
      pendingDateMs: null,
      selectedIndex: null,
      weatherData: {},
      loadingState: {},
      pendingRequests: {},
      needsRouteSync: false,
      fitRouteOnNextSync: false,
      suppressNextMapFocus: false,
      lastRouteCoordinates: [],
      lastHighlightedPointId: null,
      routeCanvas: null,
      routeCtx: null,
      routeOverlayHandlers: {
        move: null,
        resize: null,
        windowResize: null,
        click: null,
        mousemove: null
      },
      weatherPrefetchPromise: null,
      weatherPrefetchCancel: false
    };
  },
  computed: {
    ...mapStores(useRoutesStore, useDeviceStore, useFleetStore),
    waypoints() {
      return this.routesStore.getShipWaypoints || [];
    },
    hasRoute() {
      return Array.isArray(this.waypoints) && this.waypoints.length > 0;
    },
    routeStartUnix() {
      const date = this.routesStore.getShipStartDate;
      const time = this.routesStore.getShipStartTime;
      if (!date || !time) {
        return null;
      }

      const parsed = dayjs(`${date} ${time}`, 'YYYY-MM-DD HH:mm');
      return parsed.isValid() ? parsed.unix() : null;
    },
    shipNameLabel() {
      const deviceName = this.deviceStore?.getShipName;
      const routeName = this.routesStore?.getRouteName;
      return deviceName || routeName || '';
    },
    shipNameDisplay() {
      return this.shipNameLabel || 'N/A';
    },
    hasOwnShipPosition() {
      const pos = this.fleetStore?.selectedShipPosition;
      return Boolean(pos && Number.isFinite(Number(pos.lat)) && Number.isFinite(Number(pos.lon)));
    },
  },
  watch: {
    waypoints: {
      handler() {
        this.resetWeatherCache();
        this.scheduleRouteSync();
      },
      deep: true
    },
    hasRoute(newValue) {
      if (!newValue) {
        this.selectedIndex = null;
        this.clearRouteOverlays();
      } else {
        this.scheduleRouteSync();
      }
    }
  },
  mounted() {
    this.initMeteoblueMap();
    if (this.hasRoute) {
      this.scheduleRouteSync();
    }
  },
  beforeDestroy() {
    this.cancelWeatherPrefetch();
    this.destroyMeteoblueMap();
  },
  methods: {
    centerMapOnShip() {
      if (!this.hasOwnShipPosition || !this.mapboxMap) {
        return;
      }
      const pos = this.fleetStore.selectedShipPosition;
      const lat = Number(pos.lat);
      const lon = Number(pos.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        return;
      }
      try {
        this.mapboxMap.flyTo({
          center: [lon, lat],
          zoom: Math.max(this.mapboxMap.getZoom(), 6),
          duration: 600,
        });
      } catch (e) {
        console.error('centerMapOnShip error:', e);
      }
    },
    async initMeteoblueMap() {
      try {
        const { mapboxgl } = await meteoblueLoader.load();
        const container = this.$refs.mapContainer;
        if (!container) {
          return;
        }

        this.mapboxgl = mapboxgl;
        this.mapboxMap = new mapboxgl.Map({
          container,
          center: [20, 60],
          zoom: 4,
          minZoom: 1,
          maxZoom: 12,
          hash: false,
          attributionControl: false,
          keyboard: false
        });

        this.mapboxMap.addControl(new mapboxgl.NavigationControl({ showZoom: true, showCompass: false }), 'top-right');

        const inventoryUrl = this.buildInventoryUrl();
        this.meteobluePlugin = new window.meteoblueMapsPlugin({
          mapboxMap: this.mapboxMap,
          inventory: inventoryUrl,
          apiKey: METEOBLUE_API_KEY,
          canUseRestricted: true,
          language: 'en',
          showSidebar: 'auto'
        });

        if (this.meteobluePlugin && typeof this.meteobluePlugin.on === 'function') {
          const onPluginLoad = () => {
            this.applyPendingDate();
            if (this.meteobluePlugin && typeof this.meteobluePlugin.off === 'function') {
              this.meteobluePlugin.off('load', onPluginLoad);
            }
            this.meteobluePluginLoadHandler = null;
          };
          this.meteobluePluginLoadHandler = onPluginLoad;
          this.meteobluePlugin.on('load', onPluginLoad);

          const onMenuChange = event => {
            console.log(LOG_PREFIX, 'menuchange event', event?.map?.id, event?.variant?.id, event?.overlays?.map(o => o.id));
            this.drawRouteOverlay();
          };
          this.meteobluePluginMenuHandler = onMenuChange;
          this.meteobluePlugin.on('menuchange', onMenuChange);
        }

        this.mapboxMap.on('load', () => {
          console.log(LOG_PREFIX, 'map load event fired');
          this.mapReady = true;
          this.initRouteOverlay();
          this.bindRouteInteractions();
          this.trySyncRoute();

          // Обновляем маркер корабля после загрузки карты
          this.updateShipMarker();

          if (this.pendingDateMs) {
            this.applyPendingDate();
          }
        });

        this.mapStyleLoadHandler = () => {
          console.log(LOG_PREFIX, 'style.load event observed');
          this.handleMapStyleReload();
        };
        this.mapboxMap.on('style.load', this.mapStyleLoadHandler);

        this.$nextTick(() => {
          if (this.mapboxMap) {
            this.mapboxMap.resize();
          }
        });
      } catch (error) {
        console.error('Failed to initialize meteoblue map:', error);
      }
    },
    destroyMeteoblueMap() {
      this.cancelWeatherPrefetch();
      this.removeRouteInteractions();
      this.destroyRouteOverlay();
      if (this.mapStyleLoadHandler && this.mapboxMap) {
        this.mapboxMap.off('style.load', this.mapStyleLoadHandler);
      }

      if (this.meteobluePlugin) {
        try {
          if (typeof this.meteobluePlugin.destroy === 'function') {
            this.meteobluePlugin.destroy();
          } else if (typeof this.meteobluePlugin.remove === 'function') {
            this.meteobluePlugin.remove();
          }
        } catch (error) {
          console.warn('Unable to dispose meteoblue plugin:', error);
        }
      }
      if (this.mapboxMap) {
        this.mapboxMap.remove();
      }
      this.mapboxMap = null;
      this.mapboxgl = null;
      if (this.meteobluePlugin && typeof this.meteobluePlugin.off === 'function') {
        if (this.meteobluePluginLoadHandler) {
          this.meteobluePlugin.off('load', this.meteobluePluginLoadHandler);
        }
        if (this.meteobluePluginMenuHandler) {
          this.meteobluePlugin.off('menuchange', this.meteobluePluginMenuHandler);
        }
      }
      this.meteobluePlugin = null;
      this.meteobluePluginLoadHandler = null;
      this.meteobluePluginMenuHandler = null;
      this.mapStyleLoadHandler = null;
      this.mapReady = false;
      this.pendingDateMs = null;
      this.lastRouteCoordinates = [];
      this.lastHighlightedPointId = null;
      const container = this.$refs.mapContainer;
      if (container) {
        container.innerHTML = '';
      }
    },
    handleMapStyleReload() {
      if (!this.mapReady) {
        return;
      }
      this.initRouteOverlay();
      this.drawRouteOverlay();
      this.updateShipMarker();
    },
    buildInventoryUrl() {
      return METEOBLUE_INVENTORY_URL;
    },
    scheduleRouteSync() {
      this.needsRouteSync = true;
      this.fitRouteOnNextSync = true;
      this.suppressNextMapFocus = false;
      this.trySyncRoute();
    },
    trySyncRoute() {
      if (!this.needsRouteSync || !this.mapReady || !this.hasRoute) {
        return;
      }

      this.needsRouteSync = false;
      this.updateRouteGeometry();
      this.ensureSelectionAfterRouteUpdate();
      // Removed automatic weather prefetching - now only fetches on row click
    },
    clearRouteOverlays() {
      this.lastRouteCoordinates = [];
      this.lastHighlightedPointId = null;
      this.drawRouteOverlay();
    },
    initRouteOverlay() {
      if (!this.mapboxMap || this.routeCanvas) {
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.className = 'route-overlay';
      canvas.style.position = 'absolute';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.pointerEvents = 'none';

      const container = this.$refs.mapContainer;
      if (container) {
        container.appendChild(canvas);
      }

      this.routeCanvas = canvas;
      this.routeCtx = canvas.getContext('2d');
      this.resizeRouteCanvas();

      if (this.mapboxMap) {
        const moveHandler = () => this.drawRouteOverlay();
        const resizeHandler = () => this.resizeRouteCanvas();
        this.mapboxMap.on('move', moveHandler);
        this.mapboxMap.on('zoom', moveHandler);
        this.mapboxMap.on('rotate', moveHandler);
        this.mapboxMap.on('pitch', moveHandler);
        this.mapboxMap.on('resize', resizeHandler);
        this.routeOverlayHandlers.move = moveHandler;
        this.routeOverlayHandlers.resize = resizeHandler;
      }

      const windowResizeHandler = () => this.resizeRouteCanvas();
      window.addEventListener('resize', windowResizeHandler);
      this.routeOverlayHandlers.windowResize = windowResizeHandler;
    },
    destroyRouteOverlay() {
      if (this.routeOverlayHandlers.move && this.mapboxMap) {
        this.mapboxMap.off('move', this.routeOverlayHandlers.move);
        this.mapboxMap.off('zoom', this.routeOverlayHandlers.move);
        this.mapboxMap.off('rotate', this.routeOverlayHandlers.move);
        this.mapboxMap.off('pitch', this.routeOverlayHandlers.move);
      }
      if (this.routeOverlayHandlers.resize && this.mapboxMap) {
        this.mapboxMap.off('resize', this.routeOverlayHandlers.resize);
      }
      if (this.routeOverlayHandlers.click && this.mapboxMap) {
        this.mapboxMap.off('click', this.routeOverlayHandlers.click);
      }
      if (this.routeOverlayHandlers.mousemove && this.mapboxMap) {
        this.mapboxMap.off('mousemove', this.routeOverlayHandlers.mousemove);
        if (this.mapboxMap.getCanvas()) {
          this.mapboxMap.getCanvas().style.cursor = '';
        }
      }
      if (this.routeOverlayHandlers.windowResize) {
        window.removeEventListener('resize', this.routeOverlayHandlers.windowResize);
      }

      if (this.routeCanvas && this.routeCanvas.parentNode) {
        this.routeCanvas.parentNode.removeChild(this.routeCanvas);
      }
      this.routeCanvas = null;
      this.routeCtx = null;
      this.routeOverlayHandlers = {
        move: null,
        resize: null,
        windowResize: null,
        click: null,
        mousemove: null
      };
    },
    resizeRouteCanvas() {
      if (!this.routeCanvas || !this.mapboxMap || !this.routeCtx) {
        return;
      }

      const rect = this.mapboxMap.getCanvas().getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      this.routeCanvas.width = rect.width * dpr;
      this.routeCanvas.height = rect.height * dpr;
      this.routeCanvas.style.width = `${rect.width}px`;
      this.routeCanvas.style.height = `${rect.height}px`;

      this.routeCtx.setTransform(1, 0, 0, 1, 0, 0);
      this.routeCtx.scale(dpr, dpr);
      this.drawRouteOverlay();
    },
    drawRouteOverlay() {
      if (!this.routeCtx || !this.mapboxMap) {
        return;
      }

      const ctx = this.routeCtx;
      const rect = this.mapboxMap.getCanvas().getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);

      if (!this.hasRoute) {
        this.drawShipMarker(ctx);
        return;
      }

      const points = this.waypoints
        .map((point, index) => {
          const lat = Number(point.lat);
          const lon = Number(point.lon);
          if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
            return null;
          }
          const projected = this.mapboxMap.project([lon, lat]);
          return projected ? { x: projected.x, y: projected.y, index } : null;
        })
        .filter(Boolean);

      if (!points.length) {
        this.drawShipMarker(ctx);
        return;
      }

      this.drawRoutePath(ctx, points);
      this.drawRoutePoints(ctx, points);
      this.drawShipMarker(ctx);
    },
    drawRoutePath(ctx, points) {
      if (points.length < 2) {
        return;
      }
      ctx.save();
      ctx.lineWidth = 3.2;
      ctx.strokeStyle = '#f97316';
      ctx.globalAlpha = 0.9;
      ctx.beginPath();
      points.forEach((pt, idx) => {
        if (idx === 0) {
          ctx.moveTo(pt.x, pt.y);
        } else {
          ctx.lineTo(pt.x, pt.y);
        }
      });
      ctx.stroke();
      ctx.restore();
    },
    drawRoutePoints(ctx, points) {
      points.forEach((pt, idx) => {
        const isStart = idx === 0;
        const isEnd = idx === points.length - 1;
        const isSelected = this.lastHighlightedPointId === idx;
        const radius = isSelected ? 7 : 5;
        const strokeWidth = isSelected ? 2 : 1.4;

        ctx.save();
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = isStart ? '#22c55e' : (isEnd ? '#ef4444' : '#334155');
        ctx.globalAlpha = 0.95;
        ctx.fill();
        ctx.lineWidth = strokeWidth;
        ctx.strokeStyle = isSelected ? '#111827' : '#f9fafb';
        ctx.stroke();
        ctx.restore();
      });
    },
    drawShipMarker(ctx) {
      if (!this.mapboxMap) {
        return;
      }
      const shipPosition = this.fleetStore?.selectedShipPosition;
      if (!shipPosition || !shipPosition.lat || !shipPosition.lon) {
        return;
      }

      const lat = Number(shipPosition.lat);
      const lon = Number(shipPosition.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        return;
      }

      const projected = this.mapboxMap.project([lon, lat]);
      if (!projected) {
        return;
      }

      drawShipIcon(ctx, {
        x: projected.x,
        y: projected.y,
        headingDeg: Number(shipPosition.cog) || 0,
        fillColor: '#1d4ed8',
        strokeColor: '#bfdbfe',
        strokeWidth: 1.4,
        alpha: 0.95
      });
    },
    bindRouteInteractions() {
      if (!this.mapboxMap) {
        return;
      }
      const clickHandler = event => {
        const index = this.findWaypointNearPoint(event.point);
        if (index !== null) {
          this.handleMarkerSelection(index);
        }
      };
      const moveHandler = event => {
        const index = this.findWaypointNearPoint(event.point);
        const canvas = this.mapboxMap.getCanvas();
        if (canvas) {
          canvas.style.cursor = index !== null ? 'pointer' : '';
        }
      };
      this.mapboxMap.on('click', clickHandler);
      this.mapboxMap.on('mousemove', moveHandler);
      this.routeOverlayHandlers.click = clickHandler;
      this.routeOverlayHandlers.mousemove = moveHandler;
    },
    removeRouteInteractions() {
      if (!this.mapboxMap) {
        this.routeOverlayHandlers.click = null;
        this.routeOverlayHandlers.mousemove = null;
        return;
      }
      if (this.routeOverlayHandlers.click) {
        this.mapboxMap.off('click', this.routeOverlayHandlers.click);
        this.routeOverlayHandlers.click = null;
      }
      if (this.routeOverlayHandlers.mousemove) {
        this.mapboxMap.off('mousemove', this.routeOverlayHandlers.mousemove);
        this.routeOverlayHandlers.mousemove = null;
        const canvas = this.mapboxMap.getCanvas();
        if (canvas) {
          canvas.style.cursor = '';
        }
      }
    },
    findWaypointNearPoint(point, radius = 12) {
      if (!this.hasRoute || !this.mapboxMap) {
        return null;
      }
      const threshold = radius * radius;
      let closestIndex = null;
      let closestDistance = Infinity;

      this.waypoints.forEach((waypoint, index) => {
        const lat = Number(waypoint.lat);
        const lon = Number(waypoint.lon);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
          return;
        }
        const projected = this.mapboxMap.project([lon, lat]);
        if (!projected) {
          return;
        }
        const dx = projected.x - point.x;
        const dy = projected.y - point.y;
        const distance = dx * dx + dy * dy;
        if (distance < threshold && distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    },
    updateShipMarker() {
      if (!this.mapReady || !this.mapboxMap) {
        return;
      }

      const shipPosition = this.fleetStore?.selectedShipPosition;
      console.log(LOG_PREFIX, 'ship marker updated', shipPosition);

      if (shipPosition && shipPosition.lat && shipPosition.lon && !this.hasRoute) {
        this.mapboxMap.easeTo({
          center: [Number(shipPosition.lon), Number(shipPosition.lat)],
          zoom: Math.max(this.mapboxMap.getZoom(), 8),
          duration: 900,
          essential: true
        });
      }

      this.drawRouteOverlay();
    },
    updateRouteGeometry() {
      if (!this.mapReady) {
        return;
      }

      const coordinates = this.waypoints
        .map(point => [Number(point.lon), Number(point.lat)])
        .filter(pair => Number.isFinite(pair[0]) && Number.isFinite(pair[1]));

      this.lastRouteCoordinates = coordinates;
      console.log(LOG_PREFIX, 'updateRouteGeometry with', coordinates.length, 'points');

      if (this.fitRouteOnNextSync && coordinates.length) {
        this.fitRouteToMap();
        this.suppressNextMapFocus = true;
        this.fitRouteOnNextSync = false;
      }

      this.drawRouteOverlay();
    },
    ensureSelectionAfterRouteUpdate() {
      if (!this.hasRoute) {
        this.selectedIndex = null;
        return;
      }

      const nextIndex = this.selectedIndex !== null && this.selectedIndex < this.waypoints.length
        ? this.selectedIndex
        : 0;

      this.selectWaypoint(nextIndex, { focusTable: true, focusMap: true, ensureData: true });
    },
    resetWeatherCache() {
      this.cancelWeatherPrefetch();
      this.weatherData = {};
      this.loadingState = {};
      this.pendingRequests = {};
    },
    handleTableRowClick(index) {
      console.log(`[weather-on-click] Row clicked: waypoint index ${index}`);
      this.selectWaypoint(index, { focusTable: false, focusMap: true, ensureData: true });
    },
    handleMarkerSelection(index) {
      this.selectWaypoint(index, { focusTable: true, focusMap: false, ensureData: true });
    },
    selectWaypoint(index, { focusTable = true, focusMap = true, ensureData = false } = {}) {
      if (index === null || index < 0 || index >= this.waypoints.length) {
        return;
      }

      this.selectedIndex = index;

      this.updatePointHighlight(index);

      if (focusMap && this.suppressNextMapFocus) {
        this.suppressNextMapFocus = false;
      } else if (focusMap) {
        this.focusMapOnIndex(index);
      }

      if (focusTable) {
        this.focusTableRow(index);
      }

      const timestamp = this.getWaypointEtaUnix(index);
      if (timestamp) {
        this.setMapDate(timestamp);
      }

      if (ensureData) {
        this.ensureWeatherData(index, timestamp);
      }
    },
    updatePointHighlight(index) {
      this.lastHighlightedPointId = index;
      this.drawRouteOverlay();
    },
    focusMapOnIndex(index) {
      if (!this.mapboxMap || !this.waypoints[index]) {
        return;
      }

      const target = this.waypoints[index];
      const lat = Number(target.lat);
      const lon = Number(target.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        return;
      }

      this.mapboxMap.easeTo({
        center: [lon, lat],
        duration: 900,
        essential: true
      });
    },
    focusTableRow(index) {
      if (this.$refs.weatherTable && typeof this.$refs.weatherTable.focusRow === 'function') {
        this.$refs.weatherTable.focusRow(index);
      }
    },
    fitRouteToMap() {
      if (!this.mapboxMap || !this.mapboxgl || !this.lastRouteCoordinates.length) {
        return;
      }
      const bounds = this.lastRouteCoordinates.reduce((acc, coordinate) => {
        if (!acc) {
          return new this.mapboxgl.LngLatBounds(coordinate, coordinate);
        }
        return acc.extend(coordinate);
      }, null);

      if (bounds) {
        this.mapboxMap.fitBounds(bounds, {
          padding: 60,
          duration: 1100,
          essential: true
        });
      }
    },
    getWaypointEtaUnix(index) {
      const legInfo = this.routesStore.getShipLegInfo(index);
      const offset = legInfo?.fromStartSeconds;

      if (typeof offset === 'number') {
        if (this.routeStartUnix !== null) {
          return this.routeStartUnix + Math.round(offset);
        }
        return Math.floor(Date.now() / 1000) + Math.round(offset);
      }

      return this.routeStartUnix;
    },
    async ensureWeatherData(index, timestampOverride = null) {
      // [weather-on-click] Check cache first - if data already loaded or loading, return it
      // This prevents duplicate requests when clicking the same waypoint multiple times
      if (this.weatherData[index] || this.pendingRequests[index]) {
        return this.pendingRequests[index] || this.weatherData[index];
      }

      const waypoint = this.waypoints[index];
      if (!waypoint) {
        return null;
      }

      const coordinates = {
        lat: Number(waypoint.lat),
        lng: Number(waypoint.lon)
      };

      const timestamp = timestampOverride || this.getWaypointEtaUnix(index) || Math.floor(Date.now() / 1000);

      this.$set(this.loadingState, index, true);
      console.log(`[weather-on-click] Fetching weather for waypoint ${index}`, { lat: coordinates.lat, lng: coordinates.lng, timestamp });

      const request = (async () => {
        try {
          const processed = await getWindyWeatherReal(coordinates, timestamp);
          const record = this.extractWeatherSnapshot(processed, timestamp);
          this.$set(this.weatherData, index, record);
          console.log(`[weather-on-click] Weather loaded for waypoint ${index}:`, record);
          return record;
        } catch (error) {
          console.error(`[weather-on-click] Failed to load weather for waypoint ${index}:`, error);
          this.$set(this.weatherData, index, {
            temperature: null,
            windSpeed: null,
            windDirection: null,
            waveHeight: null,
            error: error?.response?.data?.message || error.message || 'Unknown error',
            timestamp
          });
          return this.weatherData[index];
        } finally {
          this.$set(this.loadingState, index, false);
        }
      })();

      this.$set(this.pendingRequests, index, request);

      try {
        return await request;
      } finally {
        this.$delete(this.pendingRequests, index);
      }
    },
    setMapDate(unixSeconds) {
      if (!unixSeconds) {
        return;
      }

      const milliseconds = unixSeconds * 1000;
      this.pendingDateMs = milliseconds;

      if (!this.meteobluePlugin || typeof this.meteobluePlugin.setDate !== 'function') {
        return;
      }

      try {
        this.meteobluePlugin.setDate(milliseconds);
      } catch (error) {
        console.warn('Unable to set meteoblue date:', error);
      }
    },
    applyPendingDate() {
      if (!this.pendingDateMs) {
        return;
      }
      if (!this.meteobluePlugin || typeof this.meteobluePlugin.setDate !== 'function') {
        return;
      }

      try {
        this.meteobluePlugin.setDate(this.pendingDateMs);
      } catch (error) {
        console.warn('Unable to set meteoblue date:', error);
      }
    },
    extractWeatherSnapshot(processed, fallbackTimestamp) {
      const timestamp = Array.isArray(processed.ts) && processed.ts.length > 0
        ? processed.ts[0]
        : fallbackTimestamp;

      const temperature = this.pickValue(processed.temp_2m);
      const windSpeed = this.pickValue(processed.wind_speed_10m);
      const windU = this.pickValue(processed.wind_u_10m);
      const windV = this.pickValue(processed.wind_v_10m);
      const waveHeight = this.pickValue(processed.waves_height);

      return {
        temperature,
        windSpeed,
        windDirection: this.computeWindDirection(windV, windU),
        waveHeight,
        timestamp,
        raw: processed
      };
    },
    pickValue(array) {
      if (Array.isArray(array) && array.length > 0) {
        return Number.isFinite(array[0]) ? Math.round(array[0] * 10) / 10 : array[0];
      }
      return null;
    },
    computeWindDirection(v, u) {
      if (typeof v !== 'number' || typeof u !== 'number') {
        return null;
      }
      let angle = Math.atan2(v, u) * 180 / Math.PI;
      if (angle < 0) {
        angle += 360;
      }
      return Math.round(angle);
    },
    cancelWeatherPrefetch() {
      this.weatherPrefetchCancel = true;
      this.weatherPrefetchPromise = null;
    },
    resolvePrefetchConcurrency() {
      if (typeof navigator !== 'undefined' && Number.isFinite(navigator.hardwareConcurrency)) {
        if (navigator.hardwareConcurrency >= 16) return 8;
        if (navigator.hardwareConcurrency >= 8) return 6;
        if (navigator.hardwareConcurrency >= 4) return 4;
      }
      return 3;
    },
    prefetchWeatherForAllWaypoints() {
      if (!this.hasRoute || !Array.isArray(this.waypoints) || !this.waypoints.length) {
        return;
      }
      if (this.weatherPrefetchPromise) {
        return this.weatherPrefetchPromise;
      }

      this.weatherPrefetchCancel = false;
      const indices = this.waypoints.map((_, index) => index);
      const batchSize = this.resolvePrefetchConcurrency();

      const runPrefetch = async () => {
        for (let i = 0; i < indices.length; i += batchSize) {
          if (this.weatherPrefetchCancel) {
            break;
          }
          const slice = indices.slice(i, i + batchSize);
          await Promise.all(slice.map(idx => this.ensureWeatherData(idx).catch(() => null)));
          if (this.weatherPrefetchCancel) {
            break;
          }
          await new Promise(resolve => setTimeout(resolve, 25));
        }
      };

      this.weatherPrefetchPromise = runPrefetch().finally(() => {
        this.weatherPrefetchPromise = null;
      });

      return this.weatherPrefetchPromise;
    }
  }
};
</script>

<style scoped>
@import '@/assets/styles/components/buttons.css';

.weather-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

.weather-layout {
  display: flex;
  flex-direction: row;
  height: calc(100vh - 90px);
  min-height: 520px;
  background: #f8f9fb;
  border-radius: 12px;
  overflow: hidden;
}

.weather-sidebar {
  width: 380px;
  max-width: 420px;
  background: #ffffff;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.weather-map {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
}

.weather-map__header {
  display: flex;
  align-items: center;
  padding: 8px 20px 4px;
  background: #ffffff;
  border-bottom: 1px solid #dce3ef;
}

.meteoblue-container {
  flex: 1;
  height: 100%;
  width: 100%;
  position: relative;
}

.weather-map__placeholder {
  position: absolute;
  top: 60px;
  left: 16px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.9rem;
  color: #445b78;
  display: flex;
  align-items: center;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

:deep(.mapboxgl-canvas) {
  outline: none;
}

:deep(.mapboxgl-ctrl-top-right) {
  margin: 16px;
}

:deep(.meteoblue-mapsplugin) {
  font-family: inherit;
}

@media (max-width: 1400px) {
  .weather-sidebar {
    width: 320px;
  }
}

@media (max-width: 1100px) {
  .weather-layout {
    flex-direction: column;
  }

  .weather-sidebar {
    width: 100%;
    max-width: none;
    border-right: none;
    border-bottom: 1px solid #e2e8f0;
  }

  .weather-map {
    min-height: 420px;
  }
}
</style>